import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import tenantService from '../services/tenantService';
import lookupService from '../services/lookupService';
import { darkColors, lightColors } from '../theme';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { colors, getStatusColors, getTableRowBackground, getTableHeaderStyles, getActualTableRowBackground } from '../theme/colors';

const Tenants = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const theme = useTheme();
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tenants, setTenants] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [error, setError] = useState(null);

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.tenants || response);
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchClientTypes = async () => {
    try {
      const response = await lookupService.getAllLookupValues('_tip_klijenta');
      setClientTypes(response || []);
    } catch (error) {
      console.error('Error fetching client types:', error);
      setClientTypes([]);
    }
  };

  const handleEdit = (tenant) => {
    navigate(`/tenant-edit/${tenant._id}`);
  };


  const handleDelete = async (id) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj tenant?')) {
      try {
        await tenantService.deleteTenant(id);
        // Ažuriraj tabelu bez refresh-a
        const updateTenantsSilently = async () => {
          try {
            const response = await tenantService.getAllTenants();
            setTenants(response.tenants || response);
          } catch (error) {
            setError(error.message);
          }
        };
        updateTenantsSilently();
      } catch (error) {
        setError(error.message);
      }
    }
  };

  // Učitavanje podataka
  useEffect(() => {
    const initializeData = async () => {
      await fetchClientTypes();
      await fetchTenants();
    };
    initializeData();
  }, []);

  // Event listener za otvaranje dijaloga iz header-a
  useEffect(() => {
    const handleOpenTenantDialog = () => {
      navigate("/tenant-edit/new");
    };

    window.addEventListener('openTenantDialog', handleOpenTenantDialog);
    return () => {
      window.removeEventListener('openTenantDialog', handleOpenTenantDialog);
    };
  }, [navigate]);

  // Proveri da li je korisnik super admin
  if (!currentUser || currentUser.userType !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2, ...commonStyles.alert('error') }}>
          {error}
        </Alert>
      )}

      {/* Add Tenant Button - samo za desktop */}
      {!isMobile && (
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/tenant-edit/new")}
            sx={commonStyles.gradientButton}
          >
            Novi
          </Button>
        </Box>
      )}

      {/* Responsive prikaz - tabela za desktop, kartice za mobilne */}
        {isMobile ? (
          // Mobilni prikaz - kartice
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {tenants.map((tenant, index) => (
              <Card key={tenant._id} sx={{
                ...commonStyles.paper,
                backgroundColor: getTableRowBackground(index, isDarkMode),
                border: `1px solid ${customColors.border.main}`,
                borderRadius: 2,
                p: 2
              }}>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  {/* Header sa nazivom i akcijama */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <BusinessIcon sx={{ mr: 1, color: customColors.primary.main, fontSize: '1.2rem' }} />
                      <Typography variant="body1" sx={{ ...commonStyles.textPrimary, fontWeight: 600 }}>
                        {tenant.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(tenant)}
                        sx={{
                          color: customColors.primary.main,
                          '&:hover': { backgroundColor: `${customColors.primary.main}20` }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(tenant._id)}
                        sx={{
                          color: colors.actions.delete,
                          '&:hover': { backgroundColor: `${colors.actions.delete}20` }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Tip klijenta */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={commonStyles.textSecondary}>
                      Tip klijenta:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={
                          (() => {
                            const clientType = clientTypes.find(type => type._id === tenant.clientType);
                            return clientType ? clientType.name : (tenant.businessType || 'N/A');
                          })()
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          color: customColors.primary.main,
                          borderColor: customColors.primary.main,
                          background: 'transparent',
                          '&:hover': {
                            background: `${customColors.primary.main}20`,
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Lokacija */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={commonStyles.textSecondary}>
                      Lokacija:
                    </Typography>
                    <Typography variant="body2" sx={commonStyles.textPrimary}>
                      {tenant.address?.city && tenant.address?.street 
                        ? `${tenant.address.street} ${tenant.address.number || ''}, ${tenant.address.city}`
                        : 'N/A'
                      }
                    </Typography>
                  </Box>

                  {/* Kontakt */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={commonStyles.textSecondary}>
                      Kontakt:
                    </Typography>
                    <Typography variant="body2" sx={commonStyles.textPrimary}>
                      {tenant.email || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Status */}
                  <Box>
                    <Typography variant="caption" sx={commonStyles.textSecondary}>
                      Status:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={tenant.isActive ? 'Aktivan' : 'Neaktivan'}
                        size="small"
                        sx={{
                          background: getStatusColors(tenant.isActive).background,
                          border: getStatusColors(tenant.isActive).border,
                          color: getStatusColors(tenant.isActive).light,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop prikaz - tabela
          <TableContainer sx={{ backgroundColor: isDarkMode ? 'transparent' : commonStyles.tableContainer.backgroundColor }}>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: colors.table.header.background,
                  '& .MuiTableCell-head': {
                    color: commonStyles.textPrimary.color,
                    background: getTableHeaderStyles(isDarkMode).cellBackground,
                    fontWeight: 600,
                    borderBottom: `1px solid ${customColors.border.light}`
                  }
                }}>
                  <TableCell>Naziv</TableCell>
                  <TableCell>Tip klijenta</TableCell>
                  <TableCell>Lokacija</TableCell>
                  <TableCell>Kontakt</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant, index) => (
                  <TableRow key={tenant._id} sx={{
                    backgroundColor: getActualTableRowBackground(index, isDarkMode),
                    '&:hover': {
                      background: isDarkMode 
                        ? 'rgba(75, 85, 99, 0.7)' 
                        : '#f5f5f5'
                    },
                    '& .MuiTableCell-body': {
                      borderBottom: `1px solid ${customColors.border.light}`,
                      color: commonStyles.textPrimary.color
                    }
                  }}>
                    <TableCell sx={commonStyles.textPrimary}>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon sx={{ mr: 1, color: customColors.primary.main }} />
                        {tenant.name}
                      </Box>
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      <Chip
                        label={
                          (() => {
                            const clientType = clientTypes.find(type => type._id === tenant.clientType);
                            return clientType ? clientType.name : (tenant.businessType || 'N/A');
                          })()
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          color: customColors.primary.main,
                          borderColor: customColors.primary.main,
                          background: 'transparent',
                          '&:hover': {
                            background: `${customColors.primary.main}20`,
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {tenant.address?.city && tenant.address?.street 
                        ? `${tenant.address.street} ${tenant.address.number || ''}, ${tenant.address.city}`
                        : 'N/A'
                      }
                    </TableCell>
                    <TableCell sx={commonStyles.textPrimary}>
                      {tenant.email || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.isActive ? 'Aktivan' : 'Neaktivan'}
                        size="small"
                        color={tenant.isActive ? 'success' : 'default'}
                        sx={{
                          background: tenant.isActive ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                          border: tenant.isActive ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(156, 163, 175, 0.5)',
                          color: tenant.isActive ? '#bbf7d0' : '#d1d5db',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(tenant)}
                        sx={commonStyles.iconButton(customColors.primary.main)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(tenant._id)}
                        sx={commonStyles.iconButton(colors.actions.delete)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Box>
  );
};

export default Tenants;
